import React, { type CSSProperties } from 'react';

import { type UseSortableArguments, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { UniqueIdentifier } from '@dnd-kit/core';

export type ListItemComponentType<
  T,
  TElement extends HTMLElement,
> = React.ForwardRefExoticComponent<
  ListItemComponentProps<T, TElement> & React.RefAttributes<TElement>
>;

export type ListItemComponentProps<T, TElement extends HTMLElement> = {
  item: T;
  id: UniqueIdentifier;
  isDragging?: boolean;
  isOver?: boolean;
  handleProps?: any;
  style?: CSSProperties;
} & Omit<React.HTMLAttributes<TElement>, 'id'>;

type SortableListItemProps<T, TElement extends HTMLElement> = {
  id: UniqueIdentifier;
  item: T;
  ItemComponent: ListItemComponentType<T, TElement>;
  disabled?: boolean;
  sortableProps?: Omit<UseSortableArguments, 'id'>;
};

const SortableListItemNotMemoized = function SortableListItem<T, TElement extends HTMLElement>({
  id,
  item,
  ItemComponent,
  disabled,
  sortableProps,
}: SortableListItemProps<T, TElement>) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition, isOver } =
    useSortable({
      id,
      disabled,
      ...sortableProps,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ItemComponent
      id={id}
      item={item}
      isDragging={isDragging}
      isOver={isOver}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      ref={setNodeRef as React.Ref<TElement>}
      style={style}
    />
  );
};

export const SortableListItem = React.memo(
  SortableListItemNotMemoized,
) as typeof SortableListItemNotMemoized;
